// src/pages/billing/InvoicesPage.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { projectService } from '../../services/project.service';
import { invoiceService } from '../../services/invoice.service';
import { getCompanySettings } from '../../services/companySettings.service';
import { getAllOrganizations } from '../../services/organization.service';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Invoice,
  InvoiceStatus,
  InvoiceLineItem,
  CreateInvoiceDto,
  ProjectBillingType,
  CompanySettings
} from '../../types/finance.types';
import { Project } from '../../types/project.types';
import {
  Plus,
  FileText,
  Download,
  Eye,
  Edit,
  Trash2,
  Send,
  Mail,
  Check,
  X,
  DollarSign,
  Clock,
  AlertCircle,
  Filter,
  Search
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatSafeDate } from '../../utils/dateUtils';

export default function InvoicesPage() {
  const { userProfile, currentUser } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [editMode, setEditMode] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    organizationId: '',
    organizationName: '',
    clientName: '',
    clientEmail: '',
    clientAddress: '',
    clientCity: '',
    clientState: '',
    clientZip: '',
    projectId: '',
    projectName: '',
    billingType: 'custom' as ProjectBillingType,
    taxRate: 0,
    paymentTerms: 30,
    notes: '',
    lineItems: [{ description: '', quantity: 1, unitPrice: 0, amount: 0 }] as Omit<InvoiceLineItem, 'id'>[]
  });

  const canViewInvoices = userProfile?.role === 'provider_admin';

  useEffect(() => {
    loadData();
  }, [userProfile]);

  const loadData = async () => {
    if (!userProfile || !canViewInvoices) return;

    setLoading(true);
    try {
      const [invoicesData, projectsData, settingsData, orgsData] = await Promise.all([
        invoiceService.getInvoices(),
        projectService.getUserProjects(userProfile.uid),
        getCompanySettings(),
        getAllOrganizations()
      ]);

      setInvoices(invoicesData);
      setProjects(projectsData);
      setCompanySettings(settingsData);
      setOrganizations(orgsData);

      // Set default form values from settings
      if (settingsData) {
        setFormData(prev => ({
          ...prev,
          paymentTerms: settingsData.defaultPaymentTerms || 30,
          notes: settingsData.invoiceNotes || ''
        }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      const org = organizations.find(o => o.id === project.organizationId);
      setFormData(prev => ({
        ...prev,
        projectId: project.id,
        projectName: project.name,
        organizationId: project.organizationId || '',
        organizationName: org?.name || '',
        clientName: org?.name || '',
        billingType: (project.settings?.billingInfo?.billingType as ProjectBillingType) || 'custom'
      }));
    }
  };

  const handleLineItemChange = (index: number, field: string, value: any) => {
    const newLineItems = [...formData.lineItems];
    newLineItems[index] = { ...newLineItems[index], [field]: value };

    // Calculate amount
    if (field === 'quantity' || field === 'unitPrice') {
      newLineItems[index].amount = newLineItems[index].quantity * newLineItems[index].unitPrice;
    }

    setFormData(prev => ({ ...prev, lineItems: newLineItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: 1, unitPrice: 0, amount: 0 }]
    }));
  };

  const removeLineItem = (index: number) => {
    if (formData.lineItems.length > 1) {
      setFormData(prev => ({
        ...prev,
        lineItems: prev.lineItems.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    const taxAmount = formData.taxRate ? subtotal * (formData.taxRate / 100) : 0;
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const handleCreateInvoice = async () => {
    if (!currentUser || !formData.projectId) {
      toast.error('Please select a project');
      return;
    }

    try {
      const invoiceData: CreateInvoiceDto = {
        organizationId: formData.organizationId,
        organizationName: formData.organizationName,
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        clientAddress: formData.clientAddress,
        clientCity: formData.clientCity,
        clientState: formData.clientState,
        clientZip: formData.clientZip,
        projectId: formData.projectId,
        projectName: formData.projectName,
        billingType: formData.billingType,
        lineItems: formData.lineItems.filter(item => item.description && item.unitPrice > 0),
        taxRate: formData.taxRate || undefined,
        paymentTerms: formData.paymentTerms,
        notes: formData.notes
      };

      await invoiceService.createInvoice(invoiceData, currentUser.uid);
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating invoice:', error);
    }
  };

  const handleUpdateStatus = async (invoiceId: string, status: InvoiceStatus) => {
    try {
      await invoiceService.updateInvoiceStatus(invoiceId, status);
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Email the invoice PREVIEW (exactly what the client will get, plus the
  // "Approve & Send to Client" button) to info@canvassed.com for review.
  // Approving from that email is what actually sends it to the client.
  const handleSendPreview = async (invoiceId: string) => {
    if (!window.confirm('Email a preview of this invoice to info@canvassed.com for approval? It is NOT sent to the client until you click "Approve & Send to Client" in that email.')) return;
    try {
      const fn = httpsCallable(getFunctions(), 'sendInvoicePreviewCallable');
      const res: any = await fn({ invoiceId });
      toast.success(res?.data?.message || 'Preview sent to info@canvassed.com.');
      loadData();
    } catch (error: any) {
      console.error('Error sending invoice preview:', error);
      toast.error(error?.message || 'Failed to send preview.');
    }
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await invoiceService.deleteInvoice(invoiceId);
      loadData();
    } catch (error) {
      console.error('Error deleting invoice:', error);
    }
  };

  const generateInvoicePDF = (invoice: Invoice) => {
    if (!companySettings) {
      toast.error('Please configure company settings first');
      return;
    }

    const doc = new jsPDF();

    // Header
    doc.setFillColor(79, 70, 229); // Indigo
    doc.rect(0, 0, 210, 45, 'F');

    // Company Name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(companySettings.companyName || 'Your Company', 14, 20);

    // Invoice Title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('INVOICE', 14, 32);

    // Invoice Number
    doc.setFontSize(12);
    doc.text(invoice.invoiceNumber, 14, 40);

    // Company Details (right side)
    doc.setFontSize(9);
    doc.setTextColor(200, 200, 200);
    let yPos = 15;
    if (companySettings.companyAddress) {
      doc.text(companySettings.companyAddress, 196, yPos, { align: 'right' });
      yPos += 5;
    }
    if (companySettings.companyCity || companySettings.companyState) {
      doc.text(`${companySettings.companyCity}, ${companySettings.companyState} ${companySettings.companyZip}`, 196, yPos, { align: 'right' });
      yPos += 5;
    }
    if (companySettings.companyPhone) {
      doc.text(companySettings.companyPhone, 196, yPos, { align: 'right' });
      yPos += 5;
    }
    if (companySettings.companyEmail) {
      doc.text(companySettings.companyEmail, 196, yPos, { align: 'right' });
    }

    // Reset text color
    doc.setTextColor(0, 0, 0);

    // Bill To Section
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('BILL TO:', 14, 60);
    doc.setFont('helvetica', 'normal');
    doc.text(invoice.clientName, 14, 67);
    if (invoice.clientAddress) {
      doc.text(invoice.clientAddress, 14, 73);
    }
    if (invoice.clientCity || invoice.clientState) {
      doc.text(`${invoice.clientCity || ''}, ${invoice.clientState || ''} ${invoice.clientZip || ''}`, 14, 79);
    }
    if (invoice.clientEmail) {
      doc.text(invoice.clientEmail, 14, 85);
    }

    // Invoice Details (right side)
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice Date:', 130, 60);
    doc.text('Due Date:', 130, 67);
    doc.text('Project:', 130, 74);
    doc.text('Status:', 130, 81);

    doc.setFont('helvetica', 'normal');
    doc.text(formatSafeDate(invoice.issueDate, 'MMM d, yyyy'), 170, 60);
    doc.text(formatSafeDate(invoice.dueDate, 'MMM d, yyyy'), 170, 67);
    doc.text(invoice.projectName, 170, 74);
    doc.text(invoice.status.toUpperCase(), 170, 81);

    // Line Items Table
    const tableData = invoice.lineItems.map(item => [
      item.description,
      item.quantity.toString(),
      `$${item.unitPrice.toFixed(2)}`,
      `$${item.amount.toFixed(2)}`
    ]);

    autoTable(doc, {
      head: [['Description', 'Qty', 'Unit Price', 'Amount']],
      body: tableData,
      startY: 95,
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 25, halign: 'center' },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 35, halign: 'right' }
      }
    });

    // Totals
    const finalY = (doc as any).lastAutoTable.finalY || 95;

    // Subtotal, Tax, Total
    doc.setFontSize(10);
    doc.text('Subtotal:', 140, finalY + 15);
    doc.text(`$${invoice.subtotal.toFixed(2)}`, 196, finalY + 15, { align: 'right' });

    if (invoice.taxRate && invoice.taxRate > 0) {
      doc.text(`Tax (${invoice.taxRate}%):`, 140, finalY + 22);
      doc.text(`$${invoice.taxAmount.toFixed(2)}`, 196, finalY + 22, { align: 'right' });
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    const totalY = invoice.taxRate ? finalY + 32 : finalY + 22;
    doc.text('TOTAL:', 140, totalY);
    doc.text(`$${invoice.total.toFixed(2)}`, 196, totalY, { align: 'right' });

    // Payment Information Box
    const paymentY = totalY + 20;
    doc.setFillColor(245, 245, 245);
    doc.rect(14, paymentY, 182, 45, 'F');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Information', 20, paymentY + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    let bankY = paymentY + 18;

    if (companySettings.bankName) {
      doc.text(`Bank: ${companySettings.bankName}`, 20, bankY);
      bankY += 6;
    }
    if (companySettings.bankAccountName) {
      doc.text(`Account Name: ${companySettings.bankAccountName}`, 20, bankY);
      bankY += 6;
    }
    if (companySettings.bankRoutingNumber) {
      doc.text(`Routing #: ${companySettings.bankRoutingNumber}`, 20, bankY);
      doc.text(`Account #: ${companySettings.bankAccountNumber}`, 110, bankY);
      bankY += 6;
    }
    if (companySettings.bankSwiftCode) {
      doc.text(`SWIFT: ${companySettings.bankSwiftCode}`, 20, bankY);
    }

    // Notes
    if (invoice.notes) {
      const notesY = paymentY + 55;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes:', 14, notesY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(invoice.notes, 14, notesY + 7);
    }

    // Footer
    if (companySettings.invoiceFooter) {
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(companySettings.invoiceFooter, 105, 280, { align: 'center' });
    }

    // Page 2 — per-lead billing detail + carry-over adjustments.
    const detail = invoice.billingDetail;
    if (detail && (detail.leads.length || detail.adjustments.length)) {
      doc.addPage();
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Billing Detail', 14, 20);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      doc.text(`${invoice.invoiceNumber} — ${invoice.projectName || ''}`, 14, 26);
      doc.setTextColor(0, 0, 0);

      if (detail.leads.length) {
        autoTable(doc, {
          startY: 32,
          head: [['Parent', 'Students', 'Determination', 'Amount']],
          body: detail.leads.map(l => [
            l.parentName,
            String(l.students),
            l.determination,
            `$${l.amount.toFixed(2)}`,
          ]),
          theme: 'striped',
          headStyles: { fillColor: [79, 70, 229] },
          styles: { fontSize: 9 },
        });
      }

      if (detail.adjustments.length) {
        const adjStartY = (doc as any).lastAutoTable?.finalY ? (doc as any).lastAutoTable.finalY + 10 : 40;
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('Carry-over adjustments (prior weeks)', 14, adjStartY);
        autoTable(doc, {
          startY: adjStartY + 4,
          head: [['Parent', 'Type', 'Students', 'Amount']],
          body: detail.adjustments.map(a => [
            a.parentName,
            a.kind === 'credit' ? 'Credit' : 'Carry-over',
            (a.kind === 'credit' ? '-' : '+') + String(a.students),
            `$${a.amount.toFixed(2)}`,
          ]),
          theme: 'striped',
          headStyles: { fillColor: [5, 150, 105] },
          styles: { fontSize: 9 },
        });
      }
    }

    // Page numbers (dynamic)
    const pageCount = doc.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${p} of ${pageCount}`, 105, 290, { align: 'center' });
    }

    // Save
    doc.save(`${invoice.invoiceNumber}.pdf`);
    toast.success('Invoice PDF downloaded');
  };

  const resetForm = () => {
    setFormData({
      organizationId: '',
      organizationName: '',
      clientName: '',
      clientEmail: '',
      clientAddress: '',
      clientCity: '',
      clientState: '',
      clientZip: '',
      projectId: '',
      projectName: '',
      billingType: 'custom',
      taxRate: 0,
      paymentTerms: companySettings?.defaultPaymentTerms || 30,
      notes: companySettings?.invoiceNotes || '',
      lineItems: [{ description: '', quantity: 1, unitPrice: 0, amount: 0 }]
    });
  };

  const getStatusColor = (status: InvoiceStatus) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-200 text-gray-600';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: InvoiceStatus) => {
    switch (status) {
      case 'draft': return <FileText className="h-4 w-4" />;
      case 'sent': return <Send className="h-4 w-4" />;
      case 'paid': return <Check className="h-4 w-4" />;
      case 'overdue': return <AlertCircle className="h-4 w-4" />;
      case 'cancelled': return <X className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(invoice => {
    if (statusFilter !== 'all' && invoice.status !== statusFilter) return false;
    if (projectFilter !== 'all' && invoice.projectId !== projectFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        invoice.invoiceNumber.toLowerCase().includes(query) ||
        invoice.clientName.toLowerCase().includes(query) ||
        invoice.projectName.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Calculate summary
  const summary = {
    total: invoices.reduce((sum, inv) => sum + inv.total, 0),
    paid: invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0),
    outstanding: invoices.filter(inv => inv.status === 'sent' || inv.status === 'overdue').reduce((sum, inv) => sum + inv.total, 0),
    overdue: invoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + inv.total, 0)
  };

  if (!canViewInvoices) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-gray-600 mt-2">You don't have permission to view invoices.</p>
        </div>
      </div>
    );
  }

  const { subtotal, taxAmount, total } = calculateTotals();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
            <p className="mt-1 text-sm text-gray-600">
              Create and manage client invoices
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Invoice
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Invoiced</p>
                <p className="text-2xl font-bold">${summary.total.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Paid</p>
                <p className="text-2xl font-bold text-green-600">${summary.paid.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Outstanding</p>
                <p className="text-2xl font-bold text-blue-600">${summary.outstanding.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Overdue</p>
                <p className="text-2xl font-bold text-red-600">${summary.overdue.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search invoices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-md"
                />
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | 'all')}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8">
                      <LoadingSpinner className="mx-auto" />
                    </td>
                  </tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map(invoice => (
                    <tr
                      key={invoice.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => { setSelectedInvoice(invoice); setShowViewModal(true); }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-indigo-600">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {invoice.clientName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {invoice.projectName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatSafeDate(invoice.issueDate, 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {formatSafeDate(invoice.dueDate, 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium">
                        ${invoice.total.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                          {getStatusIcon(invoice.status)}
                          <span className="ml-1 capitalize">{invoice.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setShowViewModal(true);
                            }}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                            title="View"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => generateInvoicePDF(invoice)}
                            className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          {invoice.status === 'draft' && (
                            <>
                              <button
                                onClick={() => handleSendPreview(invoice.id)}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title="Email preview to info@ for approval"
                              >
                                <Mail className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(invoice.id, 'sent')}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="Mark as Sent (no email)"
                              >
                                <Send className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteInvoice(invoice.id)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                            <button
                              onClick={() => handleUpdateStatus(invoice.id, 'paid')}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              title="Mark as Paid"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Invoice Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Create New Invoice</h2>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Project Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project *
                  </label>
                  <select
                    value={formData.projectId}
                    onChange={(e) => handleProjectChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  >
                    <option value="">Select a project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>

                {/* Client Information */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Client Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Client Name *</label>
                      <input
                        type="text"
                        value={formData.clientName}
                        onChange={(e) => setFormData(prev => ({ ...prev, clientName: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.clientEmail}
                        onChange={(e) => setFormData(prev => ({ ...prev, clientEmail: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm text-gray-700 mb-1">Address</label>
                      <input
                        type="text"
                        value={formData.clientAddress}
                        onChange={(e) => setFormData(prev => ({ ...prev, clientAddress: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        value={formData.clientCity}
                        onChange={(e) => setFormData(prev => ({ ...prev, clientCity: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-md"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">State</label>
                        <input
                          type="text"
                          value={formData.clientState}
                          onChange={(e) => setFormData(prev => ({ ...prev, clientState: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">ZIP</label>
                        <input
                          type="text"
                          value={formData.clientZip}
                          onChange={(e) => setFormData(prev => ({ ...prev, clientZip: e.target.value }))}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-medium text-gray-900">Line Items</h3>
                    <button
                      type="button"
                      onClick={addLineItem}
                      className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Line
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.lineItems.map((item, index) => (
                      <div key={index} className="flex gap-3 items-start">
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                            className="w-full px-3 py-2 border rounded-md text-sm"
                          />
                        </div>
                        <div className="w-20">
                          <input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border rounded-md text-sm"
                            min="0"
                          />
                        </div>
                        <div className="w-28">
                          <input
                            type="number"
                            placeholder="Unit Price"
                            value={item.unitPrice || ''}
                            onChange={(e) => handleLineItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 border rounded-md text-sm"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="w-28 text-right pt-2 font-medium text-sm">
                          ${(item.quantity * item.unitPrice).toFixed(2)}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          disabled={formData.lineItems.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="border-t pt-4">
                  <div className="flex justify-end">
                    <div className="w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal:</span>
                        <span className="font-medium">${subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span>Tax Rate (%):</span>
                        <input
                          type="number"
                          value={formData.taxRate || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, taxRate: parseFloat(e.target.value) || 0 }))}
                          className="w-20 px-2 py-1 border rounded text-right"
                          min="0"
                          step="0.1"
                        />
                      </div>
                      {formData.taxRate > 0 && (
                        <div className="flex justify-between text-sm">
                          <span>Tax:</span>
                          <span>${taxAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                        <span>Total:</span>
                        <span>${total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Invoice Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Terms (days)
                    </label>
                    <input
                      type="number"
                      value={formData.paymentTerms}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: parseInt(e.target.value) || 30 }))}
                      className="w-full px-3 py-2 border rounded-md"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Type
                    </label>
                    <select
                      value={formData.billingType}
                      onChange={(e) => setFormData(prev => ({ ...prev, billingType: e.target.value as ProjectBillingType }))}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="lead_generation">Lead Generation</option>
                      <option value="survey">Survey</option>
                      <option value="enrollment">Enrollment</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                    rows={3}
                    placeholder="Additional notes for the invoice..."
                  />
                </div>
              </div>

              <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-md hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateInvoice}
                  disabled={!formData.projectId || !formData.clientName || formData.lineItems.every(item => !item.description)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  Create Invoice
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Invoice Modal */}
        {showViewModal && selectedInvoice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-semibold">{selectedInvoice.invoiceNumber}</h2>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedInvoice.status)}`}>
                      {selectedInvoice.status}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedInvoice(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Bill To</h3>
                    <p className="font-medium">{selectedInvoice.clientName}</p>
                    {selectedInvoice.clientAddress && <p className="text-sm text-gray-600">{selectedInvoice.clientAddress}</p>}
                    {selectedInvoice.clientCity && (
                      <p className="text-sm text-gray-600">
                        {selectedInvoice.clientCity}, {selectedInvoice.clientState} {selectedInvoice.clientZip}
                      </p>
                    )}
                    {selectedInvoice.clientEmail && <p className="text-sm text-gray-600">{selectedInvoice.clientEmail}</p>}
                  </div>
                  <div className="text-right">
                    <div className="mb-2">
                      <p className="text-sm text-gray-500">Issue Date</p>
                      <p className="font-medium">{formatSafeDate(selectedInvoice.issueDate, 'MMM d, yyyy')}</p>
                    </div>
                    <div className="mb-2">
                      <p className="text-sm text-gray-500">Due Date</p>
                      <p className="font-medium">{formatSafeDate(selectedInvoice.dueDate, 'MMM d, yyyy')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Project</p>
                      <p className="font-medium">{selectedInvoice.projectName}</p>
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedInvoice.lineItems.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3">{item.description}</td>
                          <td className="px-4 py-3 text-right">{item.quantity}</td>
                          <td className="px-4 py-3 text-right">${item.unitPrice.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">${item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>${selectedInvoice.subtotal.toFixed(2)}</span>
                    </div>
                    {selectedInvoice.taxRate && selectedInvoice.taxRate > 0 && (
                      <div className="flex justify-between">
                        <span>Tax ({selectedInvoice.taxRate}%):</span>
                        <span>${selectedInvoice.taxAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total:</span>
                      <span>${selectedInvoice.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {selectedInvoice.notes && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Notes</h3>
                    <p className="text-sm text-gray-600">{selectedInvoice.notes}</p>
                  </div>
                )}
              </div>

              <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
                <button
                  onClick={() => generateInvoicePDF(selectedInvoice)}
                  className="px-4 py-2 border rounded-md hover:bg-gray-100 flex items-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </button>
                {selectedInvoice.status === 'draft' && (
                  <button
                    onClick={() => {
                      handleUpdateStatus(selectedInvoice.id, 'sent');
                      setShowViewModal(false);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Mark as Sent
                  </button>
                )}
                {(selectedInvoice.status === 'sent' || selectedInvoice.status === 'overdue') && (
                  <button
                    onClick={() => {
                      handleUpdateStatus(selectedInvoice.id, 'paid');
                      setShowViewModal(false);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Mark as Paid
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
